import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  TemplateRef,
  OnChanges,
  ViewChild,
  SimpleChanges,
  ChangeDetectorRef,
} from "@angular/core";
import { GridComponent } from "@syncfusion/ej2-angular-grids";
import { times } from "lodash";
import { BeforeOpenCloseEventArgs } from "@syncfusion/ej2-angular-inputs";
import { IActionMenuItem, IColumnField } from "../constant";
import { getTranslation } from "../utils/utils";
import {
  createRsCommonGridDateTreeHelper,
  DateTreeFilterHelper,
} from "../date-tree-filter/date-tree-filter.helper";

@Component({
  selector: "rs-common-grid",
  templateUrl: "./index.component.html",
  styleUrls: ["./index.component.scss"],
})
export class CommonGridComponent implements OnInit, OnChanges {
  @ViewChild("grid", { static: false })
  public grid: GridComponent;
  @Input() showCheckBox = true;
  @Input() loaded = true;
  @Input() hiddenLoaded = false;
  @Input() authorized = true; // Dataset是否授权，未授权则显示权限错误提示
  // only use for local data pagination
  @Input() selectedDiffKey = "";
  @Input() gridHeight = "";
  // 这玩意必填，不然filter columnChooser都会报错
  @Input() gridId = "grid";
  @Input() resizeSettings = { mode: "Auto" };
  @Input() filterSettings = { type: "Menu" };
  @Input() template: any;
  @Input() fields: IColumnField[] = [];
  @Input() dataSource = [];
  @Input() editSettings;
  @Input() columnTemplate: TemplateRef<any>;
  @Input() pageSettings;
  @Input() allowPaging = true;
  @Input() allowRowDragAndDrop = false;
  @Input() clipMode = "EllipsisWithTooltip";
  @Input() checkBoxWidth = 32; // col 数量过少的时候，check宽度会拉伸，这时候设置null
  @Input() childGrid;
  @Input() frozenColumns; // col 数量过少的时候，check宽度会拉伸，这时候设置null
  @Input() columnChooserSettings;
  @Input() headerTemplate: TemplateRef<any>;
  /**
	 * Sample: [
			{
				text: "Preview",
				target: ".e-content",
				id: "preview-icon",
				iconCss: "preview",
			},
			{
				text: "Download",
				target: ".e-content",
				id: "download-icon",
				iconCss: "download",
			}
		]
		用于定义row中 点击事件
	 */
  @Input() contextMenuItems: IActionMenuItem[] = [];
  // @Input() selectionSettings = { checkboxOnly: true, type: "Multiple" };
  @Input() selectionSettings = {
    type: "Multiple",
    // persistSelection: true,
    checkboxOnly: true,
  };
  @Input() alwaysShowCheckbox: boolean = false;
  @Input() defaultRecord: any;
  @Input() disableSystemRow: boolean = false; // 禁用IsSytem = true的整行
  /** 开启日期树筛选（默认关闭，不影响现有页面） */
  @Input() enableDateTreeFilter = false;
  /** 参与日期树筛选的列名或 hint，如 ['CreatedOn', 'ModifiedOn'] */
  @Input() dateTreeFilterFields: string[] = [];
  /**
   * 未筛选原始数据（可选）。
   * 不传时由组件在 dataSource 变更时自动快照；传则由页面自行维护 raw。
   */
  @Input() dateTreeSourceRows: any[] = null;
  /** 内聚模式下仍向页面同步筛选结果（默认 false，一般无需绑定） */
  @Input() dateTreeEmitFilteredRows = false;

  @Output() recordDoubleClick = new EventEmitter();
  @Output() actionComplete = new EventEmitter();
  @Output() rowSelected = new EventEmitter();
  @Output() rowDeselected = new EventEmitter();
  @Output() rowDeselecting = new EventEmitter();
  @Output() onContextMenu = new EventEmitter();
  @Output() actionHandler = new EventEmitter();
  @Output() queryCellInfo = new EventEmitter();
  @Output() recordClick = new EventEmitter();
  @Output() actionBegin = new EventEmitter();
  @Output() rowDataBound = new EventEmitter();
  @Output() dataBound = new EventEmitter();
  @Output() exportQueryCellInfo = new EventEmitter();
  @Output() rowSelecting = new EventEmitter();
  @Output() rowDrop = new EventEmitter();
  @Output() rowDrag = new EventEmitter();
  @Output() rowDragStart = new EventEmitter();
  /** 日期树筛选后的行数据（可选，复杂页面与 dateTreeSourceRows 搭配使用） */
  @Output() dateTreeFilteredRows = new EventEmitter<any[]>();

  @ViewChild("dateTreePopoverRef", { read: ElementRef, static: false })
  dateTreePopoverRef: ElementRef;

  dateTreeHelper: DateTreeFilterHelper;
  /** 内聚模式：表格实际绑定的数据 */
  dateTreeDisplayRows: any[] = [];
  private dateTreeRawRows: any[] = [];
  private dateTreeApplyingFilter = false;

  selectId = [];
  startPaging = false;
  indexList = [];
  className = "grid-loading";

  public translation;

  constructor(private ref: ChangeDetectorRef) {
    this.translation = getTranslation();
    this.dateTreeHelper = createRsCommonGridDateTreeHelper(
      this,
      (rows) => this.onDateTreeRowsApplied(rows)
    );
  }

  ngOnInit(): void {
    if (this.hiddenLoaded) this.className = "";
    this.gridId =
      this.gridId + Date.now() + "_" + Math.random().toString(36).slice(2, 5);
    this.ref && this.ref.markForCheck();
  }

  trackByFn(index, item) {
    return index; // or a unique identifier in your object
  }

  /** ejs-grid 绑定数据源：内聚模式用内部展示数据，否则用 dataSource */
  get gridBindDataSource(): any[] {
    if (this.enableDateTreeFilter && this.dateTreeSourceRows == null) {
      return this.dateTreeDisplayRows;
    }
    return this.dataSource;
  }

  /** 日期树 popover 用的全量数据 */
  get dateTreePopoverSourceRows(): any[] {
    if (this.dateTreeSourceRows != null) {
      return this.dateTreeSourceRows;
    }
    return this.dateTreeRawRows;
  }

  ngOnChanges(changes: SimpleChanges) {
    var bindRows =
      changes.dataSource && changes.dataSource.currentValue
        ? changes.dataSource.currentValue
        : null;
    if (bindRows) {
      if (
        this.allowPaging &&
        bindRows.length &&
        !this.pageSettings
      ) {
        this.pageSettings =
          bindRows.length > 9
            ? {
                pageSizes: ["All", "25", "50", "100"],
                pageSize: 50,
              }
            : null;
      }
    }
    if (
      this.enableDateTreeFilter &&
      this.dateTreeSourceRows == null &&
      changes.dataSource &&
      !this.dateTreeApplyingFilter
    ) {
      this.captureDateTreeRawFromDataSource(changes.dataSource.currentValue);
    }
    if (changes.enableDateTreeFilter && changes.enableDateTreeFilter.currentValue) {
      this.captureDateTreeRawFromDataSource(this.dataSource);
    }
    if (changes.enableDateTreeFilter && !changes.enableDateTreeFilter.currentValue) {
      this.dateTreeHelper && this.dateTreeHelper.close();
      this.dateTreeDisplayRows = [];
      this.dateTreeRawRows = [];
    }
    if (
      this.enableDateTreeFilter &&
      (changes.enableDateTreeFilter ||
        changes.dateTreeFilterFields ||
        changes.dateTreeSourceRows ||
        changes.fields ||
        changes.dataSource)
    ) {
      this.refreshDateTreeFilter();
    }
  }

  private captureDateTreeRawFromDataSource(rows: any[]) {
    this.dateTreeRawRows = rows ? rows.slice() : [];
  }

  @HostListener("document:mousedown", ["$event"])
  onDocumentMouseDown(event: MouseEvent) {
    if (this.enableDateTreeFilter && this.dateTreeHelper) {
      this.dateTreeHelper.handleDocumentMouseDown(event);
    }
  }

  @HostListener("document:mouseover", ["$event"])
  onDocumentMouseOver(event: MouseEvent) {
    if (this.enableDateTreeFilter && this.dateTreeHelper) {
      this.dateTreeHelper.handleDocumentMouseOver(event);
    }
  }

  private refreshDateTreeFilter() {
    if (!this.enableDateTreeFilter || !this.dateTreeHelper) {
      return;
    }
    var rows =
      this.dateTreeSourceRows != null
        ? this.dateTreeSourceRows
        : this.dateTreeRawRows || [];
    this.dateTreeHelper.setup({
      rows: rows,
      fields: this.fields || [],
      hints: this.dateTreeFilterFields || [],
    });
  }

  private onDateTreeRowsApplied(rows: any[]) {
    var result = rows || [];
    this.dateTreeApplyingFilter = true;
    if (this.enableDateTreeFilter && this.dateTreeSourceRows == null) {
      this.dateTreeDisplayRows = result;
    }
    if (this.dateTreeSourceRows != null || this.dateTreeEmitFilteredRows) {
      this.dateTreeFilteredRows.emit(result);
    }
    this.ref && this.ref.markForCheck();
    setTimeout(() => {
      this.dateTreeApplyingFilter = false;
    }, 0);
  }

  _load() {
    if (!this.authorized) {
      (this.grid.localeObj as any).localeStrings.EmptyRecord =
        this.translation.YOU_DO_NOT_HAVE_THE_DATASET_PERMISSION ||
        "You don't have the dataset permission to view the record.";
    }
  }

  _dataBound(args: any) {
    // 整个组件加载完成时的回调函数
    var pagerContainer = document.querySelector(".e-pagercontainer");
    if (pagerContainer) {
      var linkElements = pagerContainer.querySelectorAll(".e-link");
      linkElements.forEach(function (element) {
        element.removeAttribute("href"); // SMP2-10310 删除分页器的href属性
      });
    }
    if (this.selectedDiffKey) {
      setTimeout(() => {
        this.chooseRecords();
      });
    }
    this.dataBound.emit(args);
  }

  _exportQueryCellInfo(args) {
    this.exportQueryCellInfo.emit(args);
  }

  _rowDrag(args) {
    this.rowDrag.emit(args);
  }

  _rowDragStart(args) {
    this.rowDragStart.emit(args);
  }

  _recordDoubleClick($event) {
    this.recordDoubleClick.emit($event);
  }

  _rowDeselecting($event) {
    this.rowDeselecting.emit($event);
  }
  /**
	 * [selectionSettings]="{ checkboxOnly: true }"
	   [showCheckBox]="true"
	   控件使用时，必须配置上面两个属性，否则，全选会失效
	 */
  _rowSelected($event) {
    if (this.selectedDiffKey) {
      if ($event.isHeaderCheckboxClicked && Array.isArray($event.data)) {
        if ($event.data && $event.data.length) {
          $event.data.forEach((item) => {
            item.isSelected = true;
            this.selectId.push(item);
          });
          this.dataSource.forEach((data) => {
            $event.data.forEach((_data) => {
              if (_data[this.selectedDiffKey] === data[this.selectedDiffKey]) {
                data.isSelected = true;
              }
            });
          });
        }
      } else {
        $event.data.isSelected = true;
        this.selectId.push($event.data);
        this.dataSource.forEach((data) => {
          if (
            data[this.selectedDiffKey] === $event.data[this.selectedDiffKey]
          ) {
            data.isSelected = true;
          }
        });
      }
      this.ref && this.ref.markForCheck();
    }
    this.rowSelected.emit($event);
  }

  _rowDeselected($event) {
    if (this.selectedDiffKey) {
      if (this.startPaging) {
        return;
      }
      if ($event.isHeaderCheckboxClicked && Array.isArray($event.data)) {
        $event.data.forEach((item) => {
          item.isSelected = false;
        });
        this.selectId = [];
        this.dataSource.forEach((data) => {
          $event.data.forEach((_data) => {
            if (_data[this.selectedDiffKey] === data[this.selectedDiffKey]) {
              data.isSelected = false;
            }
          });
        });
      } else {
        $event.data.isSelected = false;
        this.selectId = this.selectId.filter(
          (item) =>
            item[this.selectedDiffKey] !== $event.data[this.selectedDiffKey]
        );
        this.dataSource.forEach((data) => {
          if (
            data[this.selectedDiffKey] === $event.data[this.selectedDiffKey]
          ) {
            data.isSelected = false;
          }
        });
      }
      this.ref && this.ref.markForCheck();
    }
    this.rowDeselected.emit($event);
  }

  _onContextMenu(args) {
    this.onContextMenu.emit(args);
  }

  _actionBegin(args) {
    if (
      this.selectedDiffKey &&
      (args.requestType === "paging" ||
        args.requestType === "searching" ||
        args.requestType === "filtering" ||
        args.requestType === "sorting")
    ) {
      this.startPaging = true;
    }
    if (this.enableDateTreeFilter && this.dateTreeHelper) {
      this.dateTreeHelper.handleGridActionBegin(args);
    } else if (
      args.requestType == "filterchoicerequest" ||
      args.requestType === "filtersearchbegin"
    ) {
      args.filterChoiceCount = 15000;
    }

    this.actionBegin.emit(args);
  }

  search(keywords) {
    this.grid.search(keywords);
  }
  selectRows(indexList: number[]) {
    this.grid.selectRows(indexList);
  }
  selectRow(index) {
    this.grid.selectRow(index);
  }
  getCurrentViewRecords() {
    return this.grid.getCurrentViewRecords();
  }

  customiseCell($event) {
    this.queryCellInfo.emit($event);
  }

  contextMenuOpen(args?: BeforeOpenCloseEventArgs): void {
    const data = (args as BeforeOpenCloseEventArgs | any).rowInfo.rowData;
    if (!data) {
      args.cancel = true;
      return;
    }
    let enableItems = [];
    let hideItems = [];
    this.grid.contextMenuModule.contextMenu.refresh(); // 每次点击按钮弹窗前需再次初始化状态 否则不更新；
    this.contextMenuItems.forEach((item: IActionMenuItem) => {
      // 用于处理部分按钮disabled的情况
      if (
        item.disabled ||
        (data.disableItem && data.disableItem.includes(item.iconCss))
      ) {
        enableItems.push(item.text);
        // this.grid.contextMenuModule.contextMenu.enableItems(
        // 	[item.text],
        // 	false
        // );
      }
      // 用于处理部分按钮需要hide的情况
      if (
        item.isHide ||
        (data.hideItem && data.hideItem.includes(item.iconCss))
      ) {
        hideItems.push(item.text);
        // this.grid.contextMenuModule.contextMenu.hideItems(
        // 	[item.text],
        // 	false
        // );
      }
    });
    this.grid.contextMenuModule.contextMenu.enableItems(enableItems, false);
    this.grid.contextMenuModule.contextMenu.hideItems(hideItems, false);
  }

  _actionHandler($event) {
    if (
      this.selectedDiffKey &&
      ($event.requestType === "paging" ||
        $event.requestType === "searching" ||
        $event.requestType === "filtering" ||
        $event.requestType === "sorting")
    ) {
      this.indexList = [];
      setTimeout(() => {
        this.chooseRecords();
        this.startPaging = false;
        this.ref && this.ref.detectChanges();
        this.ref && this.ref.markForCheck();
      });
    }
    this.actionHandler.emit($event);
    this.actionComplete.emit($event);
  }

  private chooseRecords() {
    this.indexList = [];
    this.dataSource.forEach((data) => {
      this.grid.getCurrentViewRecords().forEach((row: any, i) => {
        if (
          row &&
          data[this.selectedDiffKey] === row[this.selectedDiffKey] &&
          data.isSelected
        ) {
          this.indexList.push(i);
        }
      });
    });

    this.grid.selectRows(this.indexList);
  }

  _recordClick($event) {
    this.recordClick.emit($event);
  }
  refresh() {
    this.grid.refresh();
  }
  _rowSelecting(e) {
    this.rowSelecting.emit(e);
  }
  refreshColumns() {
    this.grid.refreshColumns();
  }
  refreshHeader() {
    this.grid.refreshHeader();
  }

  export(excelExportProperties?) {
    this.grid.excelExport(excelExportProperties);
  }

  getSelectedRecords() {
    return this.grid.getSelectedRecords();
  }

  onRowDrop(e) {
    this.rowDrop.emit(e);
  }

  _rowDataBound($event) {
    if (this.disableSystemRow && $event.data && $event.data.IsSystem) {
      $event.row.classList.add("e-disabled");
    }
    this.rowDataBound.emit($event);
  }

  clearSelection() {
    this.grid.clearSelection();
  }

  excelExport() {
    this.grid.excelExport();
  }

  addItem() {
    this.grid.addRecord({ ...this.defaultRecord, isNew: true }, 0);
    setTimeout(() => {
      this.grid.selectRow(0);
      this.grid.startEdit();
    }, 30);
  }

  saveItem() {
    this.grid.endEdit();
  }

  editItem(index) {
    setTimeout(() => {
      this.grid.selectRow(Number(index));
      this.grid.startEdit();
    }, 30);
  }

  deleteItem(index) {
    this.grid.selectRow(Number(index));
    var selectedRecord = this.grid.getSelectedRecords()[0] as any;
    this.grid.deleteRecord(selectedRecord as string);
  }

  cancelItem(index) {
    this.grid.closeEdit();
    this.deleteItem(index);
  }
  public initList = (num = 25, key = "Id") => {
    const list = [];
    times(num, (i) => {
      list.push({
        [key]: i,
      });
    });
    return list;
  };
  public showLoading() {
    this.className = "grid-loading";
    this.ref && this.ref.markForCheck();
    this.ref && this.ref.detectChanges();
  }

  public hideLoading() {
    this.className = "";
    this.ref && this.ref.markForCheck();
    this.ref && this.ref.detectChanges();
  }
}
